import * as React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch } from 'react-redux';

import { requestVideoPlayerCurrentTime } from 'actions/videoPlayer';
import { ROLLOUT_WAVEFORM } from 'constants/featureFlag';
import { V6 } from 'constants/recordingVersions';

import { useFlagIsActivated } from 'hooks/featureFlag';

import { useClipsState } from './clips/state';
import { getNextTrimmedPlayRangeValues } from './clips/util';
import { SECTION_HANDLE_LEFT } from './section-handle';
import { Clip } from '../../../../../shared/types/clips';
import {
  getHashForV6Video,
  getHashForVideo,
} from '../../../../../shared/utilities/hashing';

export type NudgeDirection = 'backward' | 'forward';

export const NUDGE_BACKWARD = 'backward';
export const NUDGE_FORWARD = 'forward';

// The react-hotkeys-hook has unique string specifications for keys. See:
// https://react-hotkeys-hook.vercel.app/docs/documentation/useHotkeys/basic-usage#modifiers--special-keys
const NUDGE_BACKWARD_HOTKEYS = 'j, left';
const NUDGE_FORWARD_HOTKEYS = 'l, right';

export const useShouldShowWaveform = (): boolean => {
  const isInWaveformFlag = useFlagIsActivated({
    flag: ROLLOUT_WAVEFORM,
    activationValues: ['show-new'],
  });

  return Boolean(isInWaveformFlag);
};

export const useIsOriginalVideoClip = (
  videoId: string,
  clips: Clip[],
  originalVideoHash: string,
  trimId: number | undefined,
  recordingVersion: string
): boolean => {
  return React.useMemo(() => {
    const currentVideoHash =
      recordingVersion !== V6
        ? getHashForVideo(videoId, trimId)
        : getHashForV6Video(videoId, clips);

    return currentVideoHash === originalVideoHash;
  }, [clips, originalVideoHash, recordingVersion, trimId, videoId]);
};

export const useEditToolsKeyboardShortcuts = (): void => {
  const dispatch = useDispatch();
  const clipsState = useClipsState(state => ({
    currentClip: state.getCurrentClip(),
    currentClipPlayRanges: state.getCurrentClip()?.playRanges || [],
    currentPlayRange: state.getCurrentPlayRanges()[state.focusedPlayRangeIndex],
    focusedPlayRangeIndex: state.focusedPlayRangeIndex,
    selectedPlayRangeHandle: state.selectedPlayRangeHandle,
    updateFocusedClipIndex: state.updateFocusedClipIndex,
    updatePlayRange: state.updatePlayRangeForClip(state.focusedClipIndex),
  }));

  const {
    currentClip,
    currentClipPlayRanges,
    currentPlayRange,
    focusedPlayRangeIndex,
    selectedPlayRangeHandle,
    updatePlayRange,
  } = clipsState;

  const handleNudge = React.useCallback(
    (nudgeDirection: NudgeDirection) => () => {
      const newPlayRange = getNextTrimmedPlayRangeValues(
        currentClipPlayRanges,
        selectedPlayRangeHandle,
        nudgeDirection,
        focusedPlayRangeIndex,
        currentClip?.duration,
        currentPlayRange
      );

      if (newPlayRange) {
        dispatch(
          requestVideoPlayerCurrentTime(
            selectedPlayRangeHandle === SECTION_HANDLE_LEFT
              ? newPlayRange.from
              : newPlayRange.to
          )
        );
        updatePlayRange(newPlayRange);
      }
    },
    [
      currentClip,
      currentClipPlayRanges,
      currentPlayRange,
      dispatch,
      focusedPlayRangeIndex,
      selectedPlayRangeHandle,
      updatePlayRange,
    ]
  );

  useHotkeys(NUDGE_BACKWARD_HOTKEYS, handleNudge(NUDGE_BACKWARD), [
    currentClip,
    currentClipPlayRanges,
    currentPlayRange,
    dispatch,
    focusedPlayRangeIndex,
    selectedPlayRangeHandle,
    updatePlayRange,
  ]);

  useHotkeys(NUDGE_FORWARD_HOTKEYS, handleNudge(NUDGE_FORWARD), [
    currentClip,
    currentClipPlayRanges,
    currentPlayRange,
    dispatch,
    focusedPlayRangeIndex,
    selectedPlayRangeHandle,
    updatePlayRange,
  ]);
};
